/**
 * Countdown reattivo sulla deadline di inattività dello store `session-idle`.
 *
 * Fuori dalla finestra di avviso non c'è nessun timer a 1 s: il valore si
 * aggiorna solo quando lo store emette (tick 10 s, attività, sync cross-tab)
 * più un timeout puntuale programmato sull'ingresso in finestra. Dentro la
 * finestra parte un tick a 1 s per il countdown `m:ss`.
 */
import { useEffect, useMemo, useState } from 'react';
import { getSessionIdleStore } from '../lib/session-idle/store';
import type { SessionIdleSnapshot, SessionIdleStore, SessionScope } from '../lib/session-idle/types';

export interface UseSessionCountdownOptions {
  readonly scope: SessionScope;
  /** Anticipo della finestra di avviso; default: `warnBeforeMs` della config dello store. */
  readonly warnBeforeMs?: number;
  /** Store alternativo (test); default: singleton dello scope. */
  readonly store?: SessionIdleStore;
}

export interface SessionCountdownState {
  /** Millisecondi residui (0 se non in corso). */
  readonly remainingMs: number;
  /** True solo con sessione in corso e residuo entro la finestra di avviso. */
  readonly isWarning: boolean;
  /** Deadline wall-clock (ms epoch) o null. */
  readonly deadline: number | null;
}

const COUNTDOWN_TICK_MS = 1_000;

function computeState(snapshot: SessionIdleSnapshot, warnBeforeMs: number | undefined): SessionCountdownState {
  const threshold = warnBeforeMs ?? snapshot.durations.warnBeforeMs;
  const running = snapshot.status === 'running' && snapshot.deadline !== null;
  const remainingMs = running ? Math.max(0, snapshot.deadline - Date.now()) : 0;
  return {
    remainingMs,
    isWarning: running && remainingMs > 0 && remainingMs <= threshold,
    deadline: running ? snapshot.deadline : null,
  };
}

export function useSessionCountdown(options: UseSessionCountdownOptions): SessionCountdownState {
  const { scope, warnBeforeMs } = options;
  const store = useMemo(() => options.store ?? getSessionIdleStore(scope), [options.store, scope]);
  const [snapshot, setSnapshot] = useState<SessionIdleSnapshot>(() => store.getSnapshot());
  const [state, setState] = useState<SessionCountdownState>(() => computeState(store.getSnapshot(), warnBeforeMs));

  // Allineamento allo store: ogni emit aggiorna snapshot e stato derivato.
  useEffect(() => {
    const sync = (next: SessionIdleSnapshot): void => {
      setSnapshot(next);
      setState(computeState(next, warnBeforeMs));
    };
    sync(store.getSnapshot());
    return store.subscribe(sync);
  }, [store, warnBeforeMs]);

  // Timer: 1 s in finestra di avviso; altrimenti un solo timeout sull'ingresso
  // in finestra (così il banner compare esattamente a `warnBeforeMs`).
  useEffect(() => {
    if (snapshot.status !== 'running' || snapshot.deadline === null) return;
    const threshold = warnBeforeMs ?? snapshot.durations.warnBeforeMs;
    const recompute = (): void => setState(computeState(store.getSnapshot(), warnBeforeMs));

    if (state.isWarning) {
      const id = setInterval(recompute, COUNTDOWN_TICK_MS);
      return () => clearInterval(id);
    }
    const untilWarning = snapshot.deadline - threshold - Date.now();
    if (untilWarning <= 0) return;
    const id = setTimeout(recompute, untilWarning);
    return () => clearTimeout(id);
  }, [store, snapshot, state.isWarning, warnBeforeMs]);

  return state;
}
